import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import PageLayout from './PageLayout';
import ProductCardSkeleton from './ProductCardSkeleton';
import SEOHead from './SEOHead';
import FAQBox from './FAQBox';
import { apiGet } from '../utils/api';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '../contexts/DarkModeContext';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: String(message), context: { location: window?.location?.pathname || '/', ...context }, ts: Date.now() }),
      keepalive: true,
    });
  } catch (_) {}
};

const PRODUCTS_PER_PAGE = 20;


// Hero Banner Component
const HeroBanner = ({ isDarkMode, filterBarRef }) => (
  <div className={`py-2 sm:py-6 lg:py-8 transition-colors duration-300 ${
    isDarkMode 
      ? 'bg-[#0f0f0f]' 
      : 'bg-gradient-to-br from-blue-50 to-white'
  }`}>
    <div className="max-w-[1525px] mx-auto px-4 sm:px-5">
      <div className="w-full flex justify-center relative" style={{zIndex: 1}}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{zIndex: 0}}>
            <img 
              src="/images/categories/youtube.svg" 
              alt="YouTube background" 
              width="320" height="320"
              style={{
                width: '200px',
                height: '200px',
                filter: 'blur(40px)',
                opacity: isDarkMode ? 0.15 : 0.12,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              className="sm:w-[320px] sm:h-[320px] sm:blur-[50px]"
              loading="eager"
              decoding="async"
            />
          </div>
        <div className={`rounded-2xl sm:rounded-3xl p-4 sm:p-8 lg:p-10 flex flex-col items-center justify-center text-white w-full mx-4 max-w-[1495px] mx-auto relative transition-all duration-500 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#0f0f0f] via-[#1a1a1a] to-[#2a2a2a] shadow-2xl shadow-black/50 border border-red-500/20' 
            : 'bg-blue-900 shadow-lg'
        }`} style={{zIndex: 1}}>
          {/* Animated background elements for dark mode */}
          {isDarkMode && (
            <div className="absolute inset-0 overflow-hidden rounded-2xl sm:rounded-3xl">
              <div className="absolute -top-6 -left-6 sm:-top-10 sm:-left-10 w-12 h-12 sm:w-20 sm:h-20 bg-red-400/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute -bottom-6 -right-6 sm:-bottom-10 sm:-right-10 w-20 h-20 sm:w-32 sm:h-32 bg-red-300/10 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
              <div className="absolute top-1/2 left-1/4 w-10 h-10 sm:w-16 sm:h-16 bg-red-500/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '2s'}}></div>
              <div className="absolute top-1/4 right-1/4 w-8 h-8 sm:w-12 sm:h-12 bg-red-600/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '0.5s'}}></div>
              <div className="absolute bottom-1/4 left-1/3 w-10 h-10 sm:w-14 sm:h-14 bg-red-700/10 rounded-full blur-lg animate-pulse" style={{animationDelay: '1.5s'}}></div>
            </div>
          )}
          
          <div className="relative z-10">
            <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mb-2 sm:mb-4 text-center transition-colors duration-300 ${
              isDarkMode 
                ? 'text-white drop-shadow-lg' 
                : 'text-white'
            }`}>
              Buy & Sell YouTube Channels
            </h1>
            <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-8 text-center max-w-2xl transition-colors duration-300 ${
              isDarkMode 
                ? 'text-red-100' 
                : 'text-blue-50'
            }`}>
              Buy monetized & non-monetized YouTube channels with secure escrow, verified sellers, and fast transfers, <span className={`font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-yellow-300' : 'text-yellow-400'
              }`}>YTProud</span> makes growth simple.
            </p>
            
            {/* Modern Feature Buttons */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-6 sm:mt-8">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-red-900/30 border-red-500/50 text-white' 
                  : 'bg-red-100 border-red-300 text-red-800'
              }`}>
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-sm font-medium">Instant Transfer</span>
              </div>
              
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-orange-900/30 border-orange-500/50 text-white' 
                  : 'bg-orange-100 border-orange-300 text-orange-800'
              }`}>
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-sm font-medium">Escrow Protection</span>
              </div>
              
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-yellow-900/30 border-yellow-500/50 text-white' 
                  : 'bg-yellow-100 border-yellow-300 text-yellow-800'
              }`}>
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-sm font-medium">Verified Accounts</span>
              </div>
              
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-red-800/30 border-red-400/50 text-white' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <span className="text-sm font-medium">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const BuyYouTubeChannel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [seoSettings, setSeoSettings] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    minPrice: '',
    maxPrice: '',
    minFollowers: '',
    maxFollowers: '',
    minIncome: '',
    maxIncome: '',
    monetization: 'all',
    sortBy: 'latest',
    search: ''
  });
  const filterBarRef = useRef(null);
  const { isDarkMode } = useDarkMode();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Fetch SEO settings for buy YouTube channel page
  useEffect(() => {
    const fetchSeoSettings = async () => {
      try {
        const res = await apiGet('/api/page-seo/page/buy-youtube-channel');
        const data = await res.json();
        setSeoSettings(data);
      } catch (error) {
        safeReportError('buy_yt_seo_fetch_failed');
      }
    };
    fetchSeoSettings();
  }, []);

  // Fetch YouTube products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Check cache first
        let cachedData = null;
        let cacheTime = null;
        try {
          cachedData = sessionStorage.getItem('youtube_products');
          cacheTime = sessionStorage.getItem('youtube_products_time');
        } catch (_) {
          safeReportError('buy_yt_cache_access_failed');
        }
        
        // Use cache if it's less than 5 minutes old
        if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
          try {
            const data = JSON.parse(cachedData);
            setProducts(Array.isArray(data) ? data : []);
          } catch (_) {
            safeReportError('buy_yt_cache_parse_failed');
            setProducts([]);
          }
          setLoading(false);
          return;
        }
        
        // Try primary endpoint: all published products
        let productsData = [];
        try {
          const res = await apiGet('/api/products?status=published&limit=50');
          if (res.ok) {
            productsData = await res.json();
          } else {
            // fall back to boosted-random if generic fetch blocked/rate-limited
            const fallback = await apiGet('/api/products/boosted-random');
            if (fallback.ok) {
              productsData = await fallback.json();
            } else {
              // propagate error if both fail
              const errText = await res.text();
              throw new Error(errText || 'Failed to fetch products');
            }
          }
        } catch (innerErr) {
          // network or CORS error; attempt fallback
          try {
            const fallback = await apiGet('/api/products/boosted-random');
            if (fallback.ok) {
              productsData = await fallback.json();
            } else {
              const errText = await fallback.text();
              throw new Error(errText || innerErr.message);
            }
          } catch (fallbackErr) {
            safeReportError('buy_yt_products_fetch_failed');
            throw fallbackErr;
          }
        }

        const products = Array.isArray(productsData) ? productsData : [];
        setProducts(products);
        
        // Cache the data
        try {
          sessionStorage.setItem('youtube_products', JSON.stringify(products));
          sessionStorage.setItem('youtube_products_time', Date.now().toString());
        } catch (_) {
          safeReportError('buy_yt_cache_write_failed');
        }
        
        setError('');
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        safeReportError('buy_yt_products_fetch_failed');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Filter products based on selected filters
  const filteredProducts = products
    .filter(product => {
      const type = String(product.accountType || product.platform || '').toLowerCase();
      return type.includes('youtube');
    })
    .filter(product => {
      if (filters.category !== 'all' && product.stats?.category !== filters.category) return false;
      if (filters.minPrice && product.price < Number(filters.minPrice)) return false;
      if (filters.maxPrice && product.price > Number(filters.maxPrice)) return false;
      if (filters.minFollowers && product.stats?.followers < Number(filters.minFollowers)) return false;
      if (filters.maxFollowers && product.stats?.followers > Number(filters.maxFollowers)) return false;
      if (filters.minIncome && product.stats?.monthlyIncome < Number(filters.minIncome)) return false;
      if (filters.maxIncome && product.stats?.monthlyIncome > Number(filters.maxIncome)) return false;
      if (filters.monetization !== 'all') {
        const monetizedStatus = product.stats?.monetized || product.stats?.monetization || product.monetized || product.monetization;
        const isMonetized = String(monetizedStatus).toLowerCase() === 'yes' || String(monetizedStatus).toLowerCase() === 'monetized' || monetizedStatus === true;
        if (filters.monetization === 'monetized' && !isMonetized) return false;
        if (filters.monetization === 'non-monetized' && isMonetized) return false;
      }
      if (filters.search && !product.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
      return true;
    });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (filters.sortBy === 'price-low-high') return a.price - b.price;
    if (filters.sortBy === 'price-high-low') return b.price - a.price;
    // Default: latest (assuming _id or createdAt desc)
    return (b.createdAt || b._id) > (a.createdAt || a._id) ? 1 : -1;
  });

  const paginatedProducts = sortedProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const renderPagination = () => (
    <div className="flex items-center gap-4 mt-6 sm:mt-10">
      {[...Array(totalPages)].map((_, idx) => (
        <button
          key={idx}
          onClick={() => setCurrentPage(idx + 1)}
          className={`w-14 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
            currentPage === idx + 1
              ? 'bg-red-600 text-white border-red-600'
              : isDarkMode
                ? 'bg-[#2a2a2a] text-gray-200 border-gray-600 hover:bg-[#3a3a3a]'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
          }`}
        >
          {idx + 1}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`w-20 h-12 rounded-full border text-lg font-medium transition-colors duration-150 focus:outline-none ${
          currentPage === totalPages
            ? isDarkMode
              ? 'bg-[#1a1a1a] text-gray-500 border-gray-700 cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
        }`}
      >
        Next
      </button>
    </div>
  );

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Open mobile filters from footer button
  useEffect(() => {
    const openHandler = () => setMobileFilterOpen(true);
    window.addEventListener('openMobileFilters', openHandler);
    return () => window.removeEventListener('openMobileFilters', openHandler);
  }, []);

  const handleResetFilters = () => {
    setFilters({
      category: 'all',
      minPrice: '',
      maxPrice: '',
      minFollowers: '',
      maxFollowers: '',
      minIncome: '',
      maxIncome: '',
      monetization: 'all',
      sortBy: 'latest',
      search: ''
    });
    setCurrentPage(1);
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead
        title={seoSettings?.title || 'Buy YouTube Channel - YTProud'}
        description={seoSettings?.description || 'Buy verified YouTube channels with subscribers and views. Secure marketplace for YouTube channel purchases.'}
        keywords={seoSettings?.keywords || 'buy youtube channel, youtube channel for sale, verified youtube channels'}
        ogTitle={seoSettings?.ogTitle || 'Buy YouTube Channel - YTProud'}
        ogDescription={seoSettings?.ogDescription || 'Buy verified YouTube channels with subscribers and views'}
        ogImage={seoSettings?.ogImage || '/images/youtube-og.jpg'}
        twitterTitle={seoSettings?.twitterTitle || 'Buy YouTube Channel - YTProud'}
        twitterDescription={seoSettings?.twitterDescription || 'Buy verified YouTube channels with subscribers and views'}
        twitterImage={seoSettings?.twitterImage || '/images/youtube-og.jpg'}
        canonical={seoSettings?.canonical || 'https://ytproud.com/buy-youtube-channel'}
        noindex={seoSettings?.noindex || false}
        nofollow={seoSettings?.nofollow || false}
        schemaEnabled={seoSettings?.schemaEnabled || false}
        schemaJsonLd={seoSettings?.customSchemaJsonLd}
      />
      <Header />
      <HeroBanner isDarkMode={isDarkMode} filterBarRef={filterBarRef} />
      <PageLayout>
        <div className="max-w-[1495px] mx-auto">
          {/* Desktop Filter Bar (visible on desktop only) */}
          <div className="hidden lg:block">
            <div className={`rounded-2xl shadow-lg p-6 lg:p-8 w-full mx-4 max-w-[1495px] mx-auto mb-4 sm:mb-8 transition-colors duration-300 ${
              isDarkMode ? 'bg-[#282828] shadow-2xl shadow-black/30' : 'bg-white'
            }`}>
            <div className="flex flex-col gap-y-4 lg:gap-y-6 w-full">
              {/* Row 1 - Search, Followers, Income, Price */}
              <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-4 items-center gap-3 lg:gap-4 xl:gap-5 w-full">
                {/* Search */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Name/Type</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none focus-within:ring-2 transition outline-none w-full text-sm lg:text-base ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-white/20' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-black/10'
                  }`}>
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                      placeholder="Search YouTube channels..."
                      className={`bg-transparent w-full px-1 py-1 outline-none border-none text-sm lg:text-base ${
                        isDarkMode ? 'text-gray-200 placeholder-gray-400' : 'text-gray-700 placeholder-gray-500'
                      }`}
                    />
                  </div>
                </div>
                {/* Followers */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Subscribers</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>👥</span>
                    <input
                      type="number"
                      name="minFollowers"
                      value={filters.minFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxFollowers"
                      value={filters.maxFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Income */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Income</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>💰</span>
                    <input
                      type="number"
                      name="minIncome"
                      value={filters.minIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxIncome"
                      value={filters.maxIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Price */}
                <div className="col-span-1 flex flex-col items-start">
                  <span className={`text-sm lg:text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Price</span>
                  <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 xl:px-5 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>$</span>
                    <input
                      type="number"
                      name="minPrice"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxPrice"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`flex-1 min-w-0 px-2 lg:px-3 py-2 rounded-lg outline-none text-sm lg:text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Row 2 - Category, Monetization, Sort, Reset */}
              <div className="grid grid-cols-1 lg:grid-cols-4 items-center gap-3 lg:gap-4 xl:gap-6 w-full">
                {/* Category Chip */}
                <div className="col-span-1">
              <select 
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
              >
                <option value="all">All Categories</option>
                <option value="Education & QA">Education & QA</option>
                <option value="Reviews & How to">Reviews & How to</option>
                <option value="Movies & Music">Movies & Music</option>
                <option value="Games">Games</option>
                <option value="Humor">Humor</option>
                <option value="Cars & Bikes">Cars & Bikes</option>
                <option value="Crypto & NFT">Crypto & NFT</option>
                <option value="Fashion & Style">Fashion & Style</option>
                <option value="Fitness & Sports">Fitness & Sports</option>
                <option value="Food">Food</option>
                <option value="Luxury & Motivation">Luxury & Motivation</option>
                <option value="Models & Celebrities">Models & Celebrities</option>
                <option value="Nature">Nature</option>
                <option value="Pets & Animals">Pets & Animals</option>
                <option value="Travel">Travel</option>
                <option value="YT Shorts & FB reels">YT Shorts & FB reels</option>
              </select>
                </div>
                {/* Monetization Chip */}
                <div className="col-span-1">
              <select 
                name="monetization"
                value={filters.monetization}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border-none transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                    }`}
              >
                <option value="all">All Monetization</option>
                <option value="monetized">Monetized</option>
                <option value="non-monetized">Non-Monetized</option>
              </select>
                </div>
                {/* Sort By Chip */}
                <div className="col-span-1">
                  <select
                    name="sortBy"
                    value={filters.sortBy}
                onChange={handleFilterChange}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-semibold shadow-sm border transition outline-none text-sm lg:text-base w-full ${
                      isDarkMode ? 'bg-[#1a1a1a] text-gray-200 border-white/20 focus:ring-2 focus:ring-white/20 focus:border-white/40' : 'bg-white text-gray-900 border-black/10 focus:ring-2 focus:ring-black/10 focus:border-black/20'
                    }`}
                  >
                    <option value="latest">Latest</option>
                    <option value="price-low-high">Price: Low to High</option>
                    <option value="price-high-low">Price: High to Low</option>
                  </select>
                </div>
                {/* Reset Filters Button */}
                <div className="col-span-1">
                  <button
                    onClick={handleResetFilters}
                    className={`px-4 lg:px-6 xl:px-7 py-3 lg:py-4 rounded-full font-bold shadow-sm transition border-none text-sm lg:text-base w-full max-w-[200px] mx-auto lg:mx-0 xl:mx-0 ${
                      isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-900 text-white hover:bg-blue-800'
                    }`}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Mobile Filter Button removed as per request */}

          {/* Product Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto mt-6 sm:mt-12">
              {Array.from({ length: 20 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-600 text-lg">{error}</div>
            </div>
          ) : paginatedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-24 text-center text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
              </svg>
              <div className="text-xl font-semibold mb-2">No channels match your filters</div>
              <div className="text-base text-gray-400">Try adjusting your search or filter options.</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto">
                {paginatedProducts.map((product, i) => (
                  <ProductCard
                    key={product._id || i + (currentPage - 1) * PRODUCTS_PER_PAGE}
                    product={product}
                    width={360}
                  />
                ))}
              </div>
              {/* Pagination Controls */}
              <div className="max-w-[1495px] mx-auto">{renderPagination()}</div>
            </>
          )}
        </div>
      </PageLayout>
      {/* FAQ Section - YouTube specific */}
      <FAQBox
        title="YouTube Buying FAQs"
        faqs={[
          { 
            q: 'Can I buy a YouTube channel that is already monetized?', 
            a: 'Yes. YTProud lists verified monetized channels with 1,000+ subscribers and 4,000+ watch hours. Most are AdSense-ready, so you can connect your account and start earning right away.\n\n👉 <a href="/buy-youtube-channel" class="underline text-blue-600 hover:text-blue-800">See monetized YouTube channels for sale.</a>' 
          },
          { 
            q: 'Is it safe to buy a YouTube channel from YTProud?', 
            a: 'Yes. Buying a YouTube channel on YTProud is completely safe. Every channel is manually verified, payments are held in escrow, and our team assists with ownership transfer. You get full buyer protection and exactly what you paid for.' 
          },
          { 
            q: 'Is buying a YouTube channel legal?', 
            a: 'Yes. Buying a YouTube channel is 100% legal if the transfer follows YouTube\'s policies. At YTProud, ownership changes are done securely through Google permissions. Thousands of creators and brands legally buy channels to skip the early grind and grow faster.' 
          },
          { 
            q: 'How do I become the full owner of a YouTube channel?', 
            a: 'To fully own a channel, you must be the primary owner of its Google or Brand Account. YTProud handles the transfer for you:\n\n• We add your email as a manager.\n• You get access to upload videos and manage content.\n• After 7 days (Google\'s waiting period), you become the primary owner.\n\nThis ensures a safe, verified handover for both monetized and non-monetized channels.' 
          },
          { 
            q: 'How quickly will I get access?', 
            a: 'You\'ll gain access within 24–72 hours after payment. YTProud starts the transfer immediately: first adding you as a manager, then completing full primary ownership after Google\'s 7-day waiting period.' 
          },
        ]}
      />
      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-40 transition-opacity"
            onClick={() => setMobileFilterOpen(false)}
          />
          {/* Drawer */}
          <div className={`ml-auto w-full max-w-sm h-full shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
            isDarkMode ? 'bg-[#282828] shadow-black/50' : 'bg-white'
          }`}>
            <div className={`flex items-center justify-between p-4 border-b transition-colors duration-300 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <span className={`text-lg font-bold transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Filters</span>
              <button onClick={() => setMobileFilterOpen(false)} aria-label="Close filters">
                <XMarkIcon className={`w-7 h-7 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 flex-1">
              {/* Row 1 */}
              <div className="flex flex-col gap-y-4 w-full mb-4">
                <div className="flex flex-col items-start w-full">
                  <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Name/Type</span>
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none w-full text-base ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <MagnifyingGlassIcon className={`w-5 h-5 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-400'
                    }`} />
                    <input
                      type="text"
                      name="search"
                      value={filters.search}
                      onChange={handleFilterChange}
                      placeholder="Search..."
                      className={`bg-transparent w-full px-1 py-1 outline-none border-none text-base transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-200 placeholder-gray-400' : 'text-gray-700 placeholder-gray-500'
                      }`}
                    />
                  </div>
                </div>
                {/* Category */}
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className={`px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                  }`}
                >
                  <option value="all">All Categories</option>
                  <option value="Education & QA">Education & QA</option>
                  <option value="Reviews & How to">Reviews & How to</option>
                  <option value="Movies & Music">Movies & Music</option>
                  <option value="Games">Games</option>
                  <option value="Humor">Humor</option>
                  <option value="Cars & Bikes">Cars & Bikes</option>
                  <option value="Crypto & NFT">Crypto & NFT</option>
                  <option value="Fashion & Style">Fashion & Style</option>
                  <option value="Fitness & Sports">Fitness & Sports</option>
                  <option value="Food">Food</option>
                  <option value="Luxury & Motivation">Luxury & Motivation</option>
                  <option value="Models & Celebrities">Models & Celebrities</option>
                  <option value="Nature">Nature</option>
                  <option value="Pets & Animals">Pets & Animals</option>
                  <option value="Travel">Travel</option>
                  <option value="YT Shorts & FB reels">YT Shorts & FB reels</option>
                </select>
                {/* Monetization */}
                <select
                  name="monetization"
                  value={filters.monetization}
                  onChange={handleFilterChange}
                  className={`px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus:bg-[#2a2a2a] focus:ring-2 focus:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus:bg-gray-100 focus:ring-2 focus:ring-black/10'
                  }`}
                >
                  <option value="all">All Monetization</option>
                  <option value="monetized">Monetized</option>
                  <option value="non-monetized">Non-Monetized</option>
                </select>
                {/* Sort */}
                <select
                  name="sortBy"
                  value={filters.sortBy}
                  onChange={handleFilterChange}
                  className={`px-4 py-3 rounded-full font-semibold shadow-sm border transition outline-none text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 border-white/20 focus:ring-2 focus:ring-white/20 focus:border-white/40' : 'bg-white text-gray-900 border-black/10 focus:ring-2 focus:ring-black/10 focus:border-black/20'
                  }`}
                >
                  <option value="latest">Latest</option>
                  <option value="price-low-high">Price: Low to High</option>
                  <option value="price-high-low">Price: High to Low</option>
                </select>
                {/* Subscribers */}
                <div className="flex flex-col items-start w-full">
                  <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Subscribers</span>
                  <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>👥</span>
                    <input
                      type="number"
                      name="minFollowers"
                      value={filters.minFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxFollowers"
                      value={filters.maxFollowers}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Income */}
                <div className="flex flex-col items-start w-full">
                  <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Income</span>
                  <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>💰</span>
                    <input
                      type="number"
                      name="minIncome"
                      value={filters.minIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxIncome"
                      value={filters.maxIncome}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Price */}
                <div className="flex flex-col items-start w-full">
                  <span className={`text-base font-semibold mb-1 ml-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>Price</span>
                  <div className={`flex items-center gap-6 px-4 py-3 rounded-full font-semibold shadow-sm border-none transition outline-none text-base w-full ${
                    isDarkMode ? 'bg-[#1a1a1a] text-gray-200 hover:bg-[#2a2a2a] focus-within:bg-[#2a2a2a] focus-within:ring-2 focus-within:ring-white/20' : 'bg-gray-50 text-gray-800 hover:bg-gray-100 focus-within:bg-gray-100 focus-within:ring-2 focus-within:ring-black/10'
                  }`}>
                    <span>$</span>
                    <input
                      type="number"
                      name="minPrice"
                      value={filters.minPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Min"
                      className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>-</span>
                    <input
                      type="number"
                      name="maxPrice"
                      value={filters.maxPrice}
                      onChange={handleFilterChange}
                      min="0"
                      placeholder="Max"
                      className={`w-20 px-3 py-2 rounded-lg outline-none text-base text-center shadow-sm ${
                        isDarkMode ? 'bg-[#0f0f0f] border border-white/30 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/40 focus:border-white/60' : 'bg-white border border-black/20 text-black placeholder-gray-500 focus:ring-2 focus:ring-black/20 focus:border-black/40'
                      }`}
                    />
                  </div>
                </div>
                {/* Actions */}
                <div className="flex gap-4 mt-6 md:hidden">
                  <button
                    onClick={() => { handleResetFilters(); setMobileFilterOpen(false); }}
                    className={`flex-1 px-4 py-4 rounded-full font-bold shadow-sm transition border-none text-base ${
                      isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-900 text-white hover:bg-blue-800'
                    }`}
                  >
                    Reset Filters
                  </button>
                  <button
                    onClick={() => setMobileFilterOpen(false)}
                    className={`flex-1 px-4 py-4 rounded-full font-bold shadow-sm transition border-none text-base ${
                      isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default BuyYouTubeChannel; 